import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FiSearch, FiX } from 'react-icons/fi';

import { PATHS } from '@/routes/paths';
import { matrizSaebMatrixApi } from '@/services/matrizSaebMatrix';
import useMatrizEtapas from '@/hooks/useMatrizEtapas';
import { Badge } from '@/components/ui/badge';
import { Button, ButtonIcon } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionItem, AccordionContent, AccordionTrigger, AccordionTitle, AccordionSubtitle } from '@/components/ui/accordion';
import { Table, TableBody, TableCell, TableHeaderWrapper, TableNoData, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Loader } from '@/components/custom/loader';
import { DashboardContent } from '@/layouts/dashboard';
import DescriptorDetail, { dokVariant, dificuldadeVariant } from './DescriptorDetail';

// Fonte da matriz por etapa (informativo). A lista de etapas vem do backend.
const ETAPA_SOURCE = {
  serie_3_em: 'SAEB 2001-2023',
  ano_9_ef: 'BNCC',
  ano_5_ef: 'BNCC',
  ano_2_ef: 'BNCC'
};

function StatsCards({ data }) {
  if (!data) return null;
  const comps = data.components || [];
  return (
    <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
      <StatCard
        label="Total descritores"
        value={data.total}
        accent="primary"
      />
      {comps.map(c => (
        <StatCard
          key={c.code}
          label={c.label}
          value={c.count}
          accent="neutral"
          hint={c.code}
        />
      ))}
      <StatCard
        label="Fonte"
        value={ETAPA_SOURCE[data.etapa] || '—'}
        accent="neutral"
        small
      />
    </div>
  );
}

function StatCard({ label, value, accent = 'neutral', hint, small }) {
  const ring = accent === 'primary' ? 'border-primary/20 bg-primary/5' : 'border-neutral-lightest bg-white';
  return (
    <div className={`rounded-lg border ${ring} p-3`}>
      <div className="text-xs uppercase tracking-wide text-neutral">{label}</div>
      <div className={`mt-1 font-bold text-foreground ${small ? 'text-base' : 'text-2xl'}`}>{value}</div>
      {hint && <div className="text-xs text-neutral-light">{hint}</div>}
    </div>
  );
}

const MATRIX_TABLE_HEAD = [{ label: 'Código', width: 110 }, { label: 'DOK', width: 70 }, { label: 'Dificuldade', width: 110 }, { label: 'Eixo cog.' }, { label: 'Bloom', width: 160 }, { label: 'Verbo' }, { label: 'Faixa TRI', width: 100 }];

function DescriptorsTable({ descritores, onSelect }) {
  return (
    <Table className="min-w-[900px]">
      <TableHeaderWrapper tableHead={MATRIX_TABLE_HEAD} />
      <TableBody>
        {descritores.map(d => (
          <TableRow
            key={d.code}
            onClick={() => onSelect(d)}
            className="hover:bg-primary/5 cursor-pointer"
          >
            <TableCell className="font-mono text-xs font-semibold text-primary-dark">{d.code}</TableCell>
            <TableCell>
              <Badge
                variant={dokVariant(d.dok_target)}
                size="sm"
              >
                DOK {d.dok_target}
              </Badge>
            </TableCell>
            <TableCell>
              <Badge
                variant={dificuldadeVariant(d.dif_qual)}
                size="sm"
              >
                {d.dif_qual}
              </Badge>
            </TableCell>
            <TableCell className="text-xs">{d.eixo_cog_saeb}</TableCell>
            <TableCell className="text-xs">
              {d.bloom_proc}
              <div className="text-neutral">{d.bloom_conh}</div>
            </TableCell>
            <TableCell className="text-sm">{d.verbo}</TableCell>
            <TableCell className="font-mono text-xs">{d.faixa_tri}</TableCell>
          </TableRow>
        ))}
        <TableNoData
          notFound={descritores.length === 0}
          colSpan={MATRIX_TABLE_HEAD.length}
        />
      </TableBody>
    </Table>
  );
}

export default function ReviewMatrix() {
  const { etapas, loading: loadingEtapas } = useMatrizEtapas();
  const [etapa, setEtapa] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [openSections, setOpenSections] = useState([]);
  const [selected, setSelected] = useState(null);

  const breadcrumbs = {
    title: 'Banco de questões',
    nav: [
      { route: PATHS.QUESTIONS.REVIEW_QUEUE, name: 'Revisão pedagógica', isActive: false },
      { name: 'Matriz pedagógica', isActive: true }
    ]
  };

  // Seleciona a primeira etapa retornada pelo backend.
  useEffect(() => {
    if (!etapa && etapas.length > 0) setEtapa(etapas[0].etapa);
  }, [etapa, etapas]);

  useEffect(() => {
    if (!etapa) return;
    setLoading(true);
    setData(null);
    matrizSaebMatrixApi
      .show(etapa)
      .then(json => setData(json))
      .catch(() => {
        toast.error('Falha ao carregar matriz desta etapa.');
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [etapa]);

  // Reset busca e seções colapsadas ao trocar de etapa.
  useEffect(() => {
    setSearch('');
    setOpenSections([]);
  }, [etapa]);

  const filteredGroups = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.components;
    return data.components
      .map(c => ({
        ...c,
        descritores: c.descritores.filter(d => d.code?.toLowerCase().includes(q) || d.verbo?.toLowerCase().includes(q) || d.claim?.toLowerCase().includes(q) || d.objeto?.toLowerCase().includes(q))
      }))
      .filter(c => c.descritores.length > 0);
  }, [data, search]);

  const totalShown = filteredGroups.reduce((acc, c) => acc + c.descritores.length, 0);

  const selectedComponentLabel = selected ? filteredGroups.find(c => c.descritores.includes(selected))?.label || data?.components?.find(c => c.descritores.some(d => d.code === selected.code))?.label : '';

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <div className="mb-4">
        <h2 className="mb-1 text-xl font-bold text-foreground">Matriz pedagógica — Projeto Floriano</h2>
        <p className="text-sm text-neutral">18 colunas pedagógicas por descritor (Bloom, DOK, Haladyna, ECD, TRI). Fonte da verdade da geração via IA.</p>
      </div>

      <div className="mb-4">
        <Tabs
          value={etapa}
          onValueChange={setEtapa}
        >
          <TabsList>
            {etapas.map(e => (
              <TabsTrigger
                key={e.etapa}
                value={e.etapa}
              >
                {e.label}
                <span className="ml-2 text-xs text-neutral">· {e.total_descriptors}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {loading || loadingEtapas ? (
        <Loader />
      ) : etapas.length === 0 ? (
        <div className="rounded-lg border border-neutral-lightest bg-white p-8 text-center text-sm text-neutral">Nenhuma etapa da matriz disponível.</div>
      ) : !data ? (
        <div className="rounded-lg border border-neutral-lightest bg-white p-8 text-center text-sm text-neutral">Não foi possível carregar a matriz desta etapa.</div>
      ) : (
        <>
          <StatsCards data={data} />

          <div className="mb-4 flex items-center gap-3 rounded-lg border border-neutral-lightest bg-white p-3">
            <div className="relative max-w-md flex-1">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral" />
              <Input
                type="search"
                placeholder="Buscar por código, verbo, claim, objeto..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <span className="text-xs text-neutral">
              {totalShown} de {data.total} descritores
            </span>
            {search && (
              <Button
                variant="ghost"
                size="xs"
                onClick={() => setSearch('')}
              >
                <ButtonIcon>
                  <FiX />
                </ButtonIcon>
                Limpar busca
              </Button>
            )}
          </div>

          {filteredGroups.length === 0 ? (
            <div className="rounded-lg border border-neutral-lightest bg-white p-8 text-center text-sm text-neutral">Nenhum descritor encontrado.</div>
          ) : (
            <Accordion
              type="multiple"
              value={search.trim() ? filteredGroups.map(c => c.code) : openSections}
              onValueChange={setOpenSections}
            >
              {filteredGroups.map(c => (
                <AccordionItem
                  key={c.code}
                  value={c.code}
                >
                  <AccordionTrigger>
                    <AccordionTitle>
                      {c.label} <span className="ml-1 font-normal text-neutral">· {c.code}</span>
                    </AccordionTitle>
                    <AccordionSubtitle>{c.descritores.length} descritores</AccordionSubtitle>
                  </AccordionTrigger>
                  <AccordionContent>
                    <DescriptorsTable
                      descritores={c.descritores}
                      onSelect={setSelected}
                    />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </>
      )}

      <Dialog
        open={!!selected}
        onOpenChange={isOpen => {
          if (!isOpen) setSelected(null);
        }}
      >
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-mono">{selected?.code}</DialogTitle>
            <DialogDescription>{selected?.verbo}</DialogDescription>
          </DialogHeader>
          <DescriptorDetail
            d={selected}
            componentLabel={selectedComponentLabel}
          />
        </DialogContent>
      </Dialog>
    </DashboardContent>
  );
}
